/**
 * Default copy for the home page. Mirrors src/app/(site)/page.tsx so the field
 * defaults Paul opens in admin match what's currently live.
 */

import type { OgImage } from '../og-image'
import type { IconKey } from './navigation'

export type HomePageContent = {
  seo: {
    metaTitle: string
    metaDescription: string
    ogImage: OgImage | null
  }
  hero: {
    body: string
    primaryCtaLabel: string
    primaryCtaHref: string
    trustLine: string
    conditionsLine: string
    phrases: string[]
    fixLines: string[]
    halloweenPhrase: string
    halloweenFixLine: string
  }
  why: {
    heading: string
    iconKey: IconKey
    iconImage: string | null
    body1: string
    body2: string
    ctaLabel: string
    ctaHref: string
    ctaIconKey: IconKey
  }
  services: {
    heading: string
    iconKey: IconKey
    subheading: string
    heroCard: {
      title: string
      iconKey: IconKey
      tagline: string
      body: string
      ctaLabel: string
      ctaHref: string
      ctaIconKey: IconKey
      priceSpecialLabel: string
      priceGstLabel: string
    }
    drainageCard: {
      title: string
      iconKey: IconKey
      body: string
      tagline: string
      ctaLabel: string
      ctaHref: string
      ctaIconKey: IconKey
    }
    retainingCard: {
      title: string
      iconKey: IconKey
      body: string
      tagline: string
      ctaLabel: string
      ctaHref: string
      ctaIconKey: IconKey
    }
    commercialCta: {
      title: string
      iconKey: IconKey
      body: string
      href: string
      ctaIconKey: IconKey
    }
  }
  equipment: {
    headline: string
    subhead: string
    machines: Array<{
      name: string
      spec: string
      description: string
    }>
  }
  crew: {
    greeting: string
    iconKey: IconKey
    iconImage: string | null
    bio1: string
    bio2: string
    tagline: string
    valueProps: Array<{
      number: string
      label: string
      heading: string
      body: string
    }>
    serviceArea: {
      heading: string
      body: string
      callLine: string
    }
  }
  process: {
    heading: string
    stepLabel: string
    steps: Array<{
      label: string
      detail: string
    }>
  }
  reviews: {
    heading: string
    intro: string
  }
  socialFeed: {
    enabled: boolean
    heading: string
    intro: string
    facebookLabel: string
    instagramLabel: string
  }
  finalCta: {
    heading: string
    iconKey: IconKey
    body: string
    kicker: string
    primaryCtaLabel: string
    primaryCtaHref: string
    primaryCtaIconKey: IconKey
  }
}

export const homeDefaults: HomePageContent = {
  seo: {
    metaTitle: '{siteName} | Rock Walls & Driveways, {homeBase} {state}',
    metaDescription:
      'Rock retaining walls, crushed rock driveways, excavation and site work across the {region}. Owner operated, based in {homeBase}. Free on-site quote.',
    ogImage: null,
  },
  hero: {
    body: 'Driveways washing out, retaining walls leaning, drainage that was never right to begin with. We sort the lot. Driveways, walls, drainage, excavation and site work. One crew, one quote, done properly.',
    primaryCtaLabel:
      "Don't Take Your Driveway For **Granite|purple** **From {drivewayPrice}|green**",
    primaryCtaHref: '/quote',
    trustLine:
      '16 years in the Ranges. {liabilityCover} public liability. ABN registered. Owner operated. Locally crewed.',
    conditionsLine: 'Conditions apply. Price confirmed on-site. All prices inc. GST.',
    phrases: [
      "Your Driveway's Washing Away?",
      "Your Retaining Wall's Giving Up?",
      "Your Block's a Muddy Mess?",
      "Your Driveway's Got Potholes?",
      'Your Earthworks Half-Done?',
      "Your Gravel's Down the Hill Again?",
    ],
    fixLines: ['We Can Fix That!', "We'll Fix That!", 'We Fix That!', 'We Will Fix It!'],
    halloweenPhrase: 'Happy Halloween!',
    halloweenFixLine: "Have a Rock'n One!",
  },
  why: {
    heading: 'Lettuce Exceed Your **Expectations**',
    iconKey: '',
    iconImage: null,
    body1:
      "Telling mates not to bring low cars. Dodging the same potholes every morning. Watching gravel wash down the hill after every decent rain. That retaining wall leaning more than it did last year. It's not getting better on its own.",
    body2:
      'We restore driveways, fix drainage, build retaining walls, offer landscaping, and handle the earthworks underneath. One crew, one quote, and a property you can stop apologising for.',
    ctaLabel: 'Get a Free Quote',
    ctaHref: '/quote',
    ctaIconKey: 'file-text',
  },
  services: {
    heading: 'The **Rock|purple** Solid Lineup',
    iconKey: 'wrench',
    subheading:
      "Driveways, retaining walls, drainage and earthworks. Paul's crew turns up, does the lot properly, and doesn't disappear halfway through.",
    heroCard: {
      title: 'Crushed Rock Driveways',
      iconKey: 'mountain',
      tagline: 'Grade it. Crush it. Crown it.',
      body: "Eroded driveways don't just look rough. They damage vehicles, block access, and get worse every wet season. We grade what's there, fill the ruts, lay fresh crushed rock, compact it properly, and [[jargon:crown|crown]] it for drainage. Most jobs done in a day.",
      ctaLabel: 'See the full process',
      ctaHref: '/services',
      ctaIconKey: 'arrow-right',
      priceSpecialLabel: 'special*',
      priceGstLabel: 'inc. GST',
    },
    drainageCard: {
      title: 'Drainage',
      iconKey: 'droplets',
      body: "1,400 mm of rain a year in the Ranges. That water undermines driveways, pushes against retaining walls, and pools where it shouldn't. Fix the drainage and everything else lasts. Ignore it and you're paying twice.",
      tagline: 'Half the job is where the water goes.',
      ctaLabel: 'How we do it',
      ctaHref: '/services',
      ctaIconKey: 'arrow-right',
    },
    retainingCard: {
      title: 'Retaining Walls',
      iconKey: 'map-pin',
      body: "That timber wall from the early 2000s? It's past its use-by date in Ranges soil. Rock, [[jargon:gabion|gabion]], or concrete sleeper with proper drainage behind every wall. We recommend what your slope actually needs, not what pads the quote.",
      tagline: "Timber rots in the hills. We'll fix it before it fixes itself.",
      ctaLabel: 'How we do it',
      ctaHref: '/services',
      ctaIconKey: 'arrow-right',
    },
    commercialCta: {
      title: 'Builders, Councils & Developers',
      iconKey: 'hammer',
      body: 'Site prep, civil works, infrastructure, and emergency storm response.',
      href: '/builders',
      ctaIconKey: 'arrow-right',
    },
  },
  equipment: {
    headline: 'WE DIG OUR RIGS',
    subhead: 'One excavator. One skid steer. One tipper. Plus a shed full of support gear.',
    machines: [
      {
        name: 'Takeuchi TB257FR',
        spec: '5.7t Compact Excavator',
        description:
          'Nearly six tonnes of dig force that spins on a dinner plate. The offset boom swings sideways to carve trenches hard against walls, fences, and footings where bigger machines just sit and watch. Fitted with an Engcon Tilt Rotator (think human wrist).',
      },
      {
        name: 'UD Quon',
        spec: '16.5t Single Axle Tipper Truck',
        description:
          'Four hundred and twenty horses pulling tonnes of rock up grades that send other tippers home early. Disc brakes on every wheel, air ride out back, and enough GCM to pull along the machine float trailer in narrow winding hills roads.',
      },
      {
        name: 'New Holland L175',
        spec: 'Skid Steer Loader',
        description:
          'The worker ant. Loading, spreading, grading, shifting stockpiles. Fitted with a grab/grading bucket for dual purpose work where access is tight or awkward.',
      },
    ],
  },
  crew: {
    greeting: "G'day. I'm Paul.",
    iconKey: '',
    iconImage: null,
    bio1: "Done right the first time. I started Rock'n Earthworks in 2018 but I've been behind the bucket for 16 years. I operate out of {homeBase} with a small local crew. When you call, you're talking to me. When it's done, my name is on the warranty. Same crew, same standard, every time.",
    bio2: 'My family and crew all live in the hills and drive these roads every day. We know what heavy rain does to a crushed rock driveway on a steep block with clay soil. The tree roots, the narrow entries, the drainage that was never right to begin with. Nobody packs up until the water goes where it should.',
    tagline: 'We **Dig|green** What We Do.',
    valueProps: [
      {
        number: '1',
        label: 'crew',
        heading: 'No Runaround.',
        body: 'You call us. We quote it. We do the work. My name is on the guarantee. No sales team, nobody to pass the buck to.',
      },
      {
        number: '2',
        label: 'Willing To Help',
        heading: "Our Name's On It.",
        body: 'The price we quote is the price you pay. No surprises on invoice day, no "unforeseen extras" tacked on at the end. If a shorter wall with proper drainage solves the problem, we\'ll tell you.',
      },
      {
        number: '3',
        label: '781 local',
        heading: 'Boulder Than The Rest.',
        body: "Challenging access, steep blocks, narrow driveways under overhanging trees. That's a Thursday. Most contractors quote high on hills jobs because they don't want them. We've been working these roads since 2018. The hills are home.",
      },
    ],
    serviceArea: {
      heading: "Rock'n Not Stopp'n",
      body: "Eastern Victoria is our patch. Cardinia Shire, Yarra Ranges, Latrobe Valley, Gippsland. From Belgrave to Bairnsdale, if there's crushed rock and a steep driveway, we'll be there.",
      callLine: 'Not sure if we come to your area? Just call and ask.',
    },
  },
  process: {
    heading: 'How It Works.',
    stepLabel: 'Step',
    steps: [
      { label: 'Call or fill in the form', detail: 'Tell us what you need. Takes two minutes.' },
      {
        label: 'Free on-site quote',
        detail: 'We come to you, assess the site, and give you a price.',
      },
      { label: 'We do the work', detail: 'One crew. Excavator, skid steer, tipper.' },
      { label: 'Job done, site clean', detail: 'We leave it better than we found it.' },
    ],
  },
  reviews: {
    heading: 'What The Neighbours Reckon',
    intro: 'Real jobs from around the hills. Driveways, walls, the lot.',
  },
  socialFeed: {
    enabled: true,
    heading: 'Fresh Off The **Tools|chartreuse**',
    intro:
      "Big rocks, terrible jokes and absolutely no serious business. Here's what happens when the crew gets hold of a camera.",
    facebookLabel: 'Follow on Facebook',
    instagramLabel: 'Follow on Instagram',
  },
  finalCta: {
    heading: "Don't Wait For The Next Washout",
    iconKey: 'cloud-rain',
    body: 'The Ranges cop 1,000 to 1,400 mm of rain a year, double inner Melbourne. Every wet season pulls rock down the hill and quietly stresses every wall on the property. The longer it sits, the more the fix costs.',
    kicker: 'Down to **Earthworks|chartreuse**?',
    primaryCtaLabel: 'Get a **Free|chartreuse** Quote',
    primaryCtaHref: '/quote',
    primaryCtaIconKey: 'file-text',
  },
}
